/**
 * ECG line canvas – inspired by github.com/tripolskypetr/ecg-line-webcomponent
 * Draws a scrolling heartbeat trace in pink. Call bang() to trigger a beat (or use internal timer).
 */
import React, { useRef, useEffect } from 'react';

const PINK = '#d6336c';
const HEARTBEAT_SHAPE = [
  0, 0, 0, 0, 0, 0, 2, 4, 2, 0, -2, -4, -8, -12, -6, 4, 12, 18, 14, 8, 4, 2, 0, 0, 2, 4, 2, 0, 0, 0, 0, 0, 0, 0,
];
const BEAT_INTERVAL_MS = 2500;
/** Seconds for the line to scroll one full canvas width – keeps perceived speed same on all screen sizes */
const SCROLL_DURATION_SEC = 15;
const FPS = 50;
const LINE_HEIGHT = 80;

function EcgLine({ width = 800, height = 200, color = PINK, className = '' }) {
  const canvasRef = useRef(null);
  const pointsRef = useRef([]);
  const beatPhaseRef = useRef(0);
  const beatTimerRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    const midY = height / 2;
    const speed = width / (SCROLL_DURATION_SEC * FPS);
    const maxPoints = Math.ceil(width / speed) + 50;

    function getNextY() {
      const phase = beatPhaseRef.current;
      if (phase <= 0) return 0;
      const idx = Math.floor(phase);
      if (idx >= HEARTBEAT_SHAPE.length) return 0;
      const a = HEARTBEAT_SHAPE[idx];
      const b = HEARTBEAT_SHAPE[Math.min(idx + 1, HEARTBEAT_SHAPE.length - 1)];
      const t = phase - idx;
      return (a + (b - a) * t) * (LINE_HEIGHT / 24);
    }

    function draw() {
      beatTimerRef.current += 16;
      if (beatTimerRef.current >= BEAT_INTERVAL_MS) {
        beatTimerRef.current = 0;
        beatPhaseRef.current = 1;
      }
      if (beatPhaseRef.current > 0) {
        beatPhaseRef.current += 0.6;
        if (beatPhaseRef.current > HEARTBEAT_SHAPE.length) beatPhaseRef.current = 0;
      }

      const nextY = midY - getNextY();
      let points = pointsRef.current;
      points.push({ x: width + speed, y: nextY });
      if (points.length > maxPoints) points = points.slice(-maxPoints);
      pointsRef.current = points;

      ctx.clearRect(0, 0, width, height);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      points.forEach((p, i) => {
        p.x -= speed;
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.stroke();

      rafRef.current = requestAnimationFrame(draw);
    }

    // Seed initial flat line
    for (let x = -50; x <= width + 20; x += speed) {
      pointsRef.current.push({ x, y: midY });
    }

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [width, height, color]);

  return <canvas ref={canvasRef} className={className} width={width} height={height} aria-hidden />;
}

export default EcgLine;
